import {
  ArrowDown,
  ArrowUp,
  DotsSixVertical,
  Images,
  Trash,
} from '@phosphor-icons/react';
import type { MediaAssetDto } from '@reelplicate/api-client/model';
import {
  Badge,
  Button,
  cn,
  Dropzone,
  IconButton,
  Inline,
  Stack,
  Tab,
  TabList,
  Tabs,
  toast,
  UploadFileItem,
} from '@reelplicate/ui';
import { type DragEvent, useRef, useState } from 'react';

import { formatDuration } from '@/features/media/library';
import { formatBytes } from '@/features/media/media-api';
import { isAbort, useMediaUpload } from '@/features/media/use-media-upload';

import { fits, MAX_SLIDES, type PickedMedia, type PostType } from './form';

const ACCEPT: Record<PostType, string> = {
  reel: 'video/mp4,video/quicktime,video/webm',
  carousel: 'image/jpeg,image/png,image/webp',
};

type Upload = {
  key: string;
  name: string;
  size: number;
  progress: number;
  status: 'uploading' | 'complete';
};

/** "0:24 · 1080 × 1920 · 18.4 MB" */
const specs = (asset: MediaAssetDto) =>
  [
    asset.durationSeconds != null && formatDuration(asset.durationSeconds),
    asset.width && asset.height && `${asset.width} × ${asset.height}`,
    asset.sizeBytes > 0 && formatBytes(asset.sizeBytes),
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * The post's media: Reel or Carousel, dropped in (uploaded to the library)
 * or chosen from it. Once added, each file shows as an attached card
 * (Figma "Attached reel", 357:3493) with Replace and Remove.
 */
export function MediaSection({
  workspaceId,
  type,
  onTypeChange,
  media,
  onMediaChange,
  onChoose,
  error,
}: {
  workspaceId: string;
  type: PostType;
  onTypeChange: (type: PostType) => void;
  media: PickedMedia[];
  onMediaChange: (update: (media: PickedMedia[]) => PickedMedia[]) => void;
  onChoose: () => void;
  error?: string;
}) {
  const upload = useMediaUpload();
  const [uploads, setUploads] = useState<Upload[]>([]);
  const controllers = useRef(new Map<string, AbortController>());
  const seq = useRef(0);
  // Dragging a slide: where it came from and where it would land.
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const move = (from: number, to: number) =>
    onMediaChange((prev) => {
      if (to < 0 || to >= prev.length || from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
  const sortable = type === 'carousel' && media.length > 1;
  const patch = (key: string, next: Partial<Upload>) =>
    setUploads((all) => all.map((u) => (u.key === key ? { ...u, ...next } : u)));
  const drop = (key: string) => setUploads((all) => all.filter((u) => u.key !== key));

  const start = async (file: File) => {
    const key = `${file.name}-${(seq.current += 1)}`;
    const controller = new AbortController();
    controllers.current.set(key, controller);
    setUploads((all) => [
      ...all,
      { key, name: file.name, size: file.size, progress: 0, status: 'uploading' },
    ]);
    try {
      const asset = await upload(workspaceId, file, {
        signal: controller.signal,
        onProgress: (progress) => patch(key, { progress }),
      });
      drop(key);
      if (!fits(asset, type)) {
        toast.error(
          type === 'reel'
            ? `${file.name} is in your library, but a reel needs to be 9:16 and under 3 minutes.`
            : `${file.name} can’t be a carousel slide.`,
        );
        return;
      }
      const added = { ...asset, via: 'upload' as const };
      onMediaChange((prev) =>
        type === 'reel' ? [added] : [...prev, added].slice(0, MAX_SLIDES),
      );
    } catch (error) {
      drop(key);
      if (isAbort(error)) return;
      if (error instanceof Error && error.message.startsWith('Upload failed')) {
        toast.error(`${file.name} didn’t upload. Try again.`);
      }
    } finally {
      controllers.current.delete(key);
    }
  };

  const onFiles = (files: File[]) => {
    const room = type === 'reel' ? 1 : MAX_SLIDES - media.length;
    for (const file of files.slice(0, room)) void start(file);
  };

  const canAddMore =
    uploads.length === 0 &&
    (type === 'reel' ? media.length === 0 : media.length < MAX_SLIDES);

  return (
    <Stack as="section" aria-labelledby="post-media" gap={2}>
      <Inline gap={2}>
        <h3 id="post-media" className="text-label-lg text-text-primary flex-1">
          Media
        </h3>
        {media.length === 0 && uploads.length === 0 && (
          <Button variant="outline" size="sm" leadingIcon={Images} onClick={onChoose}>
            Choose from Media
          </Button>
        )}
        <Tabs
          value={type}
          onValueChange={(value) => {
            if (value !== type) onTypeChange(value as PostType);
          }}
        >
          <TabList variant="pill" aria-label="Post type">
            <Tab value="reel">Reel</Tab>
            <Tab value="carousel">Carousel</Tab>
          </TabList>
        </Tabs>
      </Inline>

      {media.length > 0 && (
        <Stack
          as={sortable ? 'ol' : 'div'}
          gap={2}
          aria-label={sortable ? 'Slides, in order' : undefined}
        >
          {media.map((asset, i) => (
            <Inline
              key={asset.id}
              as={sortable ? 'li' : 'div'}
              gap={4}
              draggable={sortable}
              onDragStart={(event: DragEvent<HTMLElement>) => {
                setDragging(i);
                event.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(event: DragEvent<HTMLElement>) => {
                if (dragging === null) return;
                event.preventDefault();
                setOver(i);
              }}
              onDrop={(event: DragEvent<HTMLElement>) => {
                event.preventDefault();
                if (dragging !== null) move(dragging, i);
                setDragging(null);
                setOver(null);
              }}
              onDragEnd={() => {
                setDragging(null);
                setOver(null);
              }}
              className={cn(
                'border-border-subtle rounded-lg border py-3 pr-4 transition-colors duration-[120ms]',
                sortable ? 'pl-1' : 'pl-3',
                dragging === i && 'opacity-50',
                over === i && dragging !== i && 'border-brand-primary bg-brand-subtle',
              )}
            >
              {sortable && (
                <DotsSixVertical
                  aria-hidden
                  className="text-icon-secondary size-5 shrink-0 cursor-grab"
                />
              )}
              {asset.thumbnailUrl || asset.fileUrl ? (
                <img
                  src={asset.thumbnailUrl ?? asset.fileUrl ?? undefined}
                  alt=""
                  draggable={false}
                  className="h-24 w-[54px] shrink-0 rounded-sm object-cover"
                />
              ) : (
                <span
                  aria-hidden
                  className="bg-bg-muted h-24 w-[54px] shrink-0 rounded-sm"
                />
              )}
              <Stack gap={1} className="min-w-0 flex-1">
                <span className="text-body-md-strong text-text-primary truncate">
                  {sortable ? `${i + 1}. ` : ''}
                  {asset.title}
                </span>
                <span className="text-caption text-text-secondary">{specs(asset)}</span>
                <Badge size="sm" className="w-fit">
                  {asset.via === 'media' ? 'From Media' : 'Uploaded'}
                </Badge>
              </Stack>
              {type === 'reel' && (
                <Button variant="ghost" size="sm" onClick={onChoose}>
                  Replace
                </Button>
              )}
              {sortable && (
                <>
                  <IconButton
                    icon={ArrowUp}
                    size="sm"
                    aria-label={`Move ${asset.title} earlier`}
                    disabled={i === 0}
                    onClick={() => move(i, i - 1)}
                  />
                  <IconButton
                    icon={ArrowDown}
                    size="sm"
                    aria-label={`Move ${asset.title} later`}
                    disabled={i === media.length - 1}
                    onClick={() => move(i, i + 1)}
                  />
                </>
              )}
              <IconButton
                icon={Trash}
                size="sm"
                aria-label={`Remove ${asset.title}`}
                onClick={() =>
                  onMediaChange((prev) => prev.filter((m) => m.id !== asset.id))
                }
              />
            </Inline>
          ))}
        </Stack>
      )}
      {sortable && (
        <p className="text-caption text-text-tertiary">
          Drag slides, or use the arrows, to change the order.
        </p>
      )}

      {uploads.map((u) => (
        <UploadFileItem
          key={u.key}
          name={u.name}
          size={u.size}
          status={u.status}
          progress={u.progress}
          onCancel={() => controllers.current.get(u.key)?.abort()}
        />
      ))}

      {canAddMore && (
        <Dropzone
          accept={ACCEPT[type]}
          multiple={type === 'carousel'}
          onFiles={onFiles}
          title={type === 'reel' ? 'Drag a reel here or' : 'Drag images here or'}
          caption={
            type === 'reel'
              ? 'MP4 or MOV, 9:16, up to 3 minutes'
              : `JPG, PNG or WebP, up to ${MAX_SLIDES} slides`
          }
          error={error}
          className="h-[150px] w-full"
        />
      )}
      {type === 'carousel' && media.length > 0 && media.length < MAX_SLIDES && (
        <Button
          variant="outline"
          size="sm"
          leadingIcon={Images}
          onClick={onChoose}
          className="w-fit"
        >
          Add from Media
        </Button>
      )}
    </Stack>
  );
}
